import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, Marker } from '@react-google-maps/api';
import { FiLayers } from 'react-icons/fi';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { useChargerOverlayLayers } from '../../hooks/useChargerOverlayLayers';
import DeckGLOverlay from '../charger_planning/DeckGLOverlay';
import { CHARGER_ROADMAP_STYLES, InfoWindowPopup } from '../charger_planning/ChargerGoogleMap';
import DataLayersDrawer from '../charger_planning/DataLayersDrawer';
import { logger } from '../../utils/logger';

const PILOT_CENTER = { lat: 34.055, lng: -117.545 };
export const PARCEL_SHAPE_MIN_ZOOM = 15;

const BASEMAPS = [
  { id: 'roadmap', label: 'Default' },
  { id: 'satellite', label: 'Satellite' },
];

const applyBasemap = (map, basemap) => {
  if (!map || !window.google?.maps) return;
  if (basemap === 'satellite') {
    map.setMapTypeId('hybrid');
    return;
  }
  map.setOptions({ styles: CHARGER_ROADMAP_STYLES });
  map.setMapTypeId('roadmap');
};

export default function SageMapCanvas({
  visibleFeatures,
  allFeatures,
  selectedId,
  onSelectFeature,
  mapRef,
  listingColor,
  listingSelectedColor,
  recommendedColor = '#b45309',
  recommendedSelectedColor = '#7c2d12',
  truthy,
  HoverCard,
  onMapBoundsChange,
}) {
  const { isLoaded, loadError } = useGoogleMaps();
  const containerRef = useRef(null);
  const dataLayerRef = useRef(null);
  const fittedRef = useRef(false);
  const boundsTimeoutRef = useRef(null);
  const placesServiceRef = useRef(null);
  const autocompleteInputRef = useRef(null);
  const autocompleteInstanceRef = useRef(null);
  const autocompleteStyleInjectedRef = useRef(false);

  const [mapInstance, setMapInstance] = useState(null);
  const [basemap, setBasemap] = useState('roadmap');
  const basemapRef = useRef(basemap);
  basemapRef.current = basemap;
  const [currentZoom, setCurrentZoom] = useState(12);
  const [mapBounds, setMapBounds] = useState(null);
  const [isDataLayersOpen, setIsDataLayersOpen] = useState(false);
  const [hoverState, setHoverState] = useState(null);
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeResults, setPlaceResults] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [infoWindowPosition, setInfoWindowPosition] = useState(null);
  const [selectedContentType, setSelectedContentType] = useState(null);

  const { activeDataLayerCount, dataLayerGroups, layerData, layerVisibility } = useChargerOverlayLayers({
    mapBounds,
    zoomLevel: currentZoom,
  });

  const featureCentroid = useCallback((geometry) => {
    if (!geometry) return PILOT_CENTER;
    if (geometry.type === 'Point') {
      const [lon, lat] = geometry.coordinates;
      return { lat, lng: lon };
    }
    const coords = [];
    const walk = (node) => {
      if (typeof node?.[0] === 'number') coords.push(node);
      else if (Array.isArray(node)) node.forEach(walk);
    };
    walk(geometry.coordinates);
    if (!coords.length) return PILOT_CENTER;
    const lon = coords.reduce((sum, pair) => sum + pair[0], 0) / coords.length;
    const lat = coords.reduce((sum, pair) => sum + pair[1], 0) / coords.length;
    return { lat, lng: lon };
  }, []);

  const fitCorridor = useCallback((map, features) => {
    if (!map || !features.length || fittedRef.current) return;
    const corridor = features.filter((feature) => truthy(feature.properties?.in_pilot_bbox));
    const pool = corridor.length ? corridor : features;
    const bounds = new window.google.maps.LatLngBounds();
    pool.forEach((feature) => bounds.extend(featureCentroid(feature.geometry)));
    map.fitBounds(bounds, 40);
    fittedRef.current = true;
  }, [featureCentroid, truthy]);

  const updateListingStyles = useCallback((dataLayer, selectedRecordId) => {
    if (!dataLayer) return;
    dataLayer.setStyle((feature) => {
      const props = {};
      feature.forEachProperty((value, key) => {
        props[key] = value;
      });
      const selected = props.record_id === selectedRecordId;
      const rank = Number(props.sage_recommended_rank);
      const recommended = Number.isFinite(rank) && rank >= 1 && rank <= 10;
      const color = recommended
        ? (selected ? recommendedSelectedColor : recommendedColor)
        : (selected ? listingSelectedColor : listingColor);
      const geometryType = feature.getGeometry()?.getType?.();
      if (geometryType === 'Point') {
        // Numbered Marker overlays handle recommended points; keep a subtle underlay.
        if (recommended) {
          return {
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: selected ? 7 : 5,
              fillColor: color,
              fillOpacity: 0.35,
              strokeColor: color,
              strokeWeight: 0,
            },
            zIndex: 1,
          };
        }
        return {
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: selected ? 9 : 6,
            fillColor: color,
            fillOpacity: 0.95,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          zIndex: selected ? 5 : 2,
        };
      }
      return {
        fillColor: color,
        fillOpacity: selected ? 0.45 : (recommended ? 0.32 : 0.25),
        strokeColor: color,
        strokeWeight: selected ? 3 : (recommended ? 2.25 : 1.5),
        zIndex: recommended ? 4 : 2,
      };
    });
  }, [listingColor, listingSelectedColor, recommendedColor, recommendedSelectedColor]);

  const showParcelShapes = currentZoom >= PARCEL_SHAPE_MIN_ZOOM;

  const displayFeatures = useMemo(() => {
    if (showParcelShapes) return visibleFeatures;
    return visibleFeatures.map((feature) => {
      if (!feature?.geometry || feature.geometry.type === 'Point') return feature;
      const center = featureCentroid(feature.geometry);
      return {
        ...feature,
        geometry: {
          type: 'Point',
          coordinates: [center.lng, center.lat],
        },
      };
    });
  }, [featureCentroid, showParcelShapes, visibleFeatures]);

  const recommendedMarkers = useMemo(() => {
    return visibleFeatures
      .map((feature) => {
        const props = feature.properties || {};
        const rank = Number(props.sage_recommended_rank);
        if (!Number.isFinite(rank) || rank < 1 || rank > 10) return null;
        const center = featureCentroid(feature.geometry);
        return {
          id: props.record_id,
          rank: Math.round(rank),
          position: center,
          feature,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.rank - b.rank);
  }, [featureCentroid, visibleFeatures]);

  const syncListingsLayer = useCallback(() => {
    const dataLayer = dataLayerRef.current;
    const map = mapInstance;
    if (!dataLayer || !map) return;
    const existing = [];
    dataLayer.forEach((feature) => existing.push(feature));
    existing.forEach((feature) => dataLayer.remove(feature));
    if (!displayFeatures.length) return;
    dataLayer.addGeoJson({
      type: 'FeatureCollection',
      features: displayFeatures,
    });
    updateListingStyles(dataLayer, selectedId);
  }, [displayFeatures, mapInstance, selectedId, updateListingStyles]);

  useEffect(() => {
    syncListingsLayer();
  }, [syncListingsLayer]);

  useEffect(() => {
    updateListingStyles(dataLayerRef.current, selectedId);
  }, [selectedId, updateListingStyles]);

  useEffect(() => {
    setHoverState(null);
  }, [showParcelShapes]);

  const selectBasemap = useCallback((nextBasemap) => {
    setBasemap(nextBasemap);
    if (mapInstance) applyBasemap(mapInstance, nextBasemap);
  }, [mapInstance]);

  const handleBoundsChanged = useCallback(() => {
    if (!mapInstance) return;
    if (boundsTimeoutRef.current) clearTimeout(boundsTimeoutRef.current);
    boundsTimeoutRef.current = setTimeout(() => {
      const bounds = mapInstance.getBounds();
      const zoom = mapInstance.getZoom();
      setCurrentZoom(zoom ?? 12);
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const nextBounds = {
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng(),
        };
        setMapBounds(nextBounds);
        onMapBoundsChange?.(nextBounds);
      }
    }, 300);
  }, [mapInstance, onMapBoundsChange]);

  const onMapLoad = useCallback((map) => {
    applyBasemap(map, basemapRef.current);
    setMapInstance(map);
    if (mapRef) mapRef.current = map;

    if (!dataLayerRef.current) {
      const dataLayer = new window.google.maps.Data({ map });
      dataLayerRef.current = dataLayer;

      dataLayer.addListener('click', (event) => {
        const props = {};
        event.feature.forEachProperty((value, key) => {
          props[key] = value;
        });
        onSelectFeature({ properties: props, geometry: null });
        setHoverState(null);
      });

      dataLayer.addListener('mouseover', (event) => {
        const props = {};
        event.feature.forEachProperty((value, key) => {
          props[key] = value;
        });
        const domEvent = event.domEvent;
        const rect = containerRef.current?.getBoundingClientRect();
        if (!domEvent || !rect) return;
        setHoverState({
          props,
          x: domEvent.clientX - rect.left,
          y: domEvent.clientY - rect.top - 12,
        });
      });

      dataLayer.addListener('mouseout', () => setHoverState(null));
    }

    try {
      if (window.google?.maps?.places) {
        placesServiceRef.current = new window.google.maps.places.PlacesService(map);
      }
    } catch (error) {
      logger.error('Failed to init PlacesService:', error);
    }

    handleBoundsChanged();
  }, [handleBoundsChanged, mapRef, onSelectFeature]);

  useEffect(() => {
    if (mapInstance && allFeatures.length) {
      fitCorridor(mapInstance, allFeatures);
    }
  }, [allFeatures, fitCorridor, mapInstance]);

  useEffect(() => {
    if (!isLoaded || !autocompleteInputRef.current || autocompleteInstanceRef.current) return;
    if (!window.google?.maps?.places?.Autocomplete) return;

    if (!autocompleteStyleInjectedRef.current) {
      const styleTag = document.createElement('style');
      styleTag.id = 'sage-pac-custom-style';
      styleTag.innerHTML = `
        .pac-container, .pac-item, .pac-item-query {
          font-family: 'Nunito', 'Inter', system-ui, -apple-system, sans-serif !important;
        }
      `;
      document.head.appendChild(styleTag);
      autocompleteStyleInjectedRef.current = true;
    }

    const mapCenter = mapInstance?.getCenter?.();
    const centerLat = mapCenter?.lat?.();
    const centerLng = mapCenter?.lng?.();
    const baseCenter = Number.isFinite(centerLat) && Number.isFinite(centerLng)
      ? { lat: centerLat, lng: centerLng }
      : PILOT_CENTER;

    const ac = new window.google.maps.places.Autocomplete(autocompleteInputRef.current, {
      bounds: {
        north: baseCenter.lat + 0.1,
        south: baseCenter.lat - 0.1,
        east: baseCenter.lng + 0.1,
        west: baseCenter.lng - 0.1,
      },
      componentRestrictions: { country: 'us' },
      strictBounds: false,
    });
    autocompleteInstanceRef.current = ac;

    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      const address = place.formatted_address || place.name || '';
      setPlaceQuery(address);
      setPlaceResults([]);
      setSelectedPlace(null);
      const loc = place.geometry?.location;
      if (loc && mapInstance) {
        const lat = loc.lat();
        const lng = loc.lng();
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          mapInstance.panTo({ lat, lng });
          mapInstance.setZoom(Math.max(15, mapInstance.getZoom?.() ?? 15));
        }
      }
    });

    return () => {
      if (listener?.remove) listener.remove();
      autocompleteInstanceRef.current = null;
    };
  }, [isLoaded, mapInstance]);

  const handlePlacesSearch = useCallback(() => {
    if (!placesServiceRef.current || !mapInstance) return;
    const query = (placeQuery || '').trim();
    if (!query) return;
    const bounds = mapInstance.getBounds();
    placesServiceRef.current.textSearch(
      { query, bounds: bounds || undefined, region: 'us' },
      (results, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results) {
          setPlaceResults([]);
          setSelectedPlace(null);
          return;
        }
        const mapped = results.map((result) => ({
          id: result.place_id,
          name: result.name || result.formatted_address || 'Place',
          position: { lat: result.geometry?.location?.lat(), lng: result.geometry?.location?.lng() },
          address: result.formatted_address || '',
          rating: result.rating,
          businessStatus: result.business_status,
        })).filter((place) => place.position?.lat && place.position?.lng);
        setPlaceResults(mapped);
        setSelectedPlace(null);
        if (mapped.length > 0) {
          const fitBounds = new window.google.maps.LatLngBounds();
          mapped.forEach((place) => fitBounds.extend(place.position));
          mapInstance.fitBounds(fitBounds);
        }
      },
    );
  }, [mapInstance, placeQuery]);

  const closeInfoWindow = useCallback(() => {
    setSelectedParcel(null);
    setInfoWindowPosition(null);
    setSelectedContentType(null);
  }, []);

  const openLayerInfo = useCallback((object, coordinate, contentType) => {
    setSelectedParcel(object);
    setSelectedContentType(contentType);
    if (coordinate && coordinate.length >= 2) {
      setInfoWindowPosition({ lat: coordinate[1], lng: coordinate[0] });
    }
  }, []);

  const handleMhdMarkerClick = useCallback((infrastructure, event) => {
    openLayerInfo(infrastructure, event?.coordinate, 'mhdInfrastructure');
    if (!event?.coordinate && infrastructure) {
      setInfoWindowPosition({
        lat: parseFloat(infrastructure.lat),
        lng: parseFloat(infrastructure.lon),
      });
    }
  }, [openLayerInfo]);

  const handleTruckStopClick = useCallback((stop, event) => {
    openLayerInfo(stop, event?.coordinate, 'truckStop');
    if (!event?.coordinate && stop) {
      setInfoWindowPosition({
        lat: parseFloat(stop.latitude),
        lng: parseFloat(stop.longitude),
      });
    }
  }, [openLayerInfo]);

  const handleTrafficClick = useCallback((traffic, event) => {
    openLayerInfo(traffic, event?.coordinate, 'traffic');
    if (!event?.coordinate) {
      const locationMatch = traffic.location?.match(/POINT\s*\(\s*([^)]+)\s*\)/);
      if (locationMatch) {
        const [lng, lat] = locationMatch[1].split(/\s+/).map(Number);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          setInfoWindowPosition({ lat, lng });
        }
      }
    }
  }, [openLayerInfo]);

  const handleCircuitClick = useCallback((circuit, coordinate) => {
    openLayerInfo(circuit, coordinate, 'circuit');
  }, [openLayerInfo]);

  const handleWarehouseClick = useCallback((warehouse, coordinate) => {
    openLayerInfo(warehouse, coordinate, 'warehouse');
  }, [openLayerInfo]);

  const handleUtilityClick = useCallback((utility, coordinate) => {
    openLayerInfo(utility, coordinate, 'utility');
  }, [openLayerInfo]);

  const handleCalEnviroScreenClick = useCallback((tract, coordinate) => {
    openLayerInfo(tract, coordinate, 'calEnviroScreen');
  }, [openLayerInfo]);

  const handleSraClick = useCallback((sra, coordinate) => {
    openLayerInfo(sra, coordinate, 'sra');
  }, [openLayerInfo]);

  if (!isLoaded) {
    return <div className="flex h-full items-center justify-center text-gray-600">Loading Google Maps...</div>;
  }
  if (loadError) {
    return <div className="flex h-full items-center justify-center text-red-600">Error loading Google Maps: {loadError.message}</div>;
  }

  const infoTitle = selectedContentType === 'circuit'
    ? 'Circuit Information'
    : selectedContentType === 'mhdInfrastructure'
      ? 'Charging Station Information'
      : selectedContentType === 'warehouse'
        ? 'Warehouse Information'
        : selectedContentType === 'traffic'
          ? 'Traffic Information'
          : selectedContentType === 'truckStop'
            ? 'Truck Stop Information'
            : selectedContentType === 'utility'
              ? 'Utility Information'
              : selectedContentType === 'calEnviroScreen'
                ? 'CalEnviroScreen Tract Information'
                : selectedContentType === 'sra'
                  ? 'SRA Information'
                  : 'Information';

  return (
    <div ref={containerRef} className="relative h-full w-full">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={PILOT_CENTER}
        zoom={12}
        onLoad={onMapLoad}
        onBoundsChanged={handleBoundsChanged}
        onClick={closeInfoWindow}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          gestureHandling: 'greedy',
        }}
      >
        {mapInstance ? (
          <DeckGLOverlay
            map={mapInstance}
            scaqmdJurisdiction={layerData.scaqmdJurisdiction}
            sraData={layerData.sraData}
            mhdInfrastructure={layerData.mhdInfrastructure}
            warehouses={layerData.warehouses}
            disadvantagedCommunities={layerData.disadvantagedCommunities}
            calEnviroScreen={layerData.calEnviroScreen}
            truckStops={layerData.truckStops}
            circuitData={layerData.circuitData}
            circuitContours={layerData.circuitContours}
            trafficData={layerData.trafficData}
            trafficStationFilters={layerData.trafficStationFilters}
            utilitiesData={layerData.utilitiesData}
            SCAQMDCheckbox={layerVisibility.SCAQMDCheckbox}
            sraCheckbox={layerVisibility.sraCheckbox}
            mhdInfrastructureCheckbox={layerVisibility.mhdInfrastructureCheckbox}
            warehouseCheckbox={layerVisibility.warehouseCheckbox}
            disadvantagedCommunitiesCheckbox={layerVisibility.disadvantagedCommunitiesCheckbox}
            calEnviroScreenCheckbox={layerVisibility.calEnviroScreenCheckbox}
            truckStopCheckbox={layerVisibility.truckStopCheckbox}
            intermodalTerminalCheckbox={layerVisibility.intermodalTerminalCheckbox}
            serviceCenterCheckbox={layerVisibility.serviceCenterCheckbox}
            circuitCapacityCheckbox={layerVisibility.circuitCapacityCheckbox}
            truckParkingLocationCheckbox={layerVisibility.truckParkingLocationCheckbox}
            pemsTrafficCheckbox={layerVisibility.pemsTrafficCheckbox}
            utilitiesCheckbox={layerVisibility.utilitiesCheckbox}
            landParcelCheckbox={layerVisibility.landParcelCheckbox}
            topParcelPoints={[]}
            onMhdMarkerClick={handleMhdMarkerClick}
            onTruckStopClick={handleTruckStopClick}
            onTrafficClick={handleTrafficClick}
            onCircuitClick={handleCircuitClick}
            onWarehouseClick={handleWarehouseClick}
            onUtilityClick={handleUtilityClick}
            onCalEnviroScreenClick={handleCalEnviroScreenClick}
            onSraClick={handleSraClick}
            currentZoom={currentZoom}
          />
        ) : null}

        {placeResults.map((place) => (
          <Marker
            key={place.id}
            position={place.position}
            title={place.name}
            onClick={() => setSelectedPlace(place)}
          />
        ))}

        {recommendedMarkers.map((marker) => {
          const selected = marker.id === selectedId;
          const color = selected ? recommendedSelectedColor : recommendedColor;
          return (
            <Marker
              key={`rec-${marker.id}`}
              position={marker.position}
              title={`#${marker.rank}`}
              zIndex={2000 - marker.rank}
              label={{
                text: String(marker.rank),
                color: '#ffffff',
                fontSize: '11px',
                fontWeight: '700',
              }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE,
                scale: selected ? 14 : 12,
                fillColor: color,
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
                labelOrigin: window.google?.maps ? new window.google.maps.Point(0, 0) : undefined,
              }}
              onClick={() => onSelectFeature(marker.feature)}
            />
          );
        })}

        {selectedPlace ? (
          <InfoWindow position={selectedPlace.position} onCloseClick={() => setSelectedPlace(null)}>
            <div className="text-sm">
              <div className="font-semibold text-gray-800">{selectedPlace.name}</div>
              {selectedPlace.address ? <div className="text-gray-600">{selectedPlace.address}</div> : null}
              {selectedPlace.rating ? <div className="text-gray-600">Rating: {selectedPlace.rating}</div> : null}
              {selectedPlace.businessStatus ? <div className="text-gray-600">Status: {selectedPlace.businessStatus}</div> : null}
            </div>
          </InfoWindow>
        ) : null}

        {selectedParcel && infoWindowPosition ? (
          <InfoWindow
            position={infoWindowPosition}
            options={{ maxWidth: selectedContentType === 'circuit' ? 380 : 336 }}
            onCloseClick={closeInfoWindow}
          >
            <div className={selectedContentType === 'circuit' ? 'p-2 min-w-[300px]' : 'p-3 max-w-sm'}>
              <h3 className="font-semibold text-gray-800 mb-2">{infoTitle}</h3>
              <div className={selectedContentType === 'circuit' ? 'text-sm' : 'space-y-1 text-sm'}>
                <InfoWindowPopup selectedContent={selectedParcel} type={selectedContentType} />
              </div>
            </div>
          </InfoWindow>
        ) : null}
      </GoogleMap>

      <div className="sage-map-search">
        <input
          ref={autocompleteInputRef}
          id="sage-pac-input"
          type="text"
          value={placeQuery}
          onChange={(e) => setPlaceQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handlePlacesSearch();
          }}
          placeholder="Search places"
        />
        <button type="button" onClick={handlePlacesSearch}>
          Go
        </button>
      </div>

      <div className="sage-basemap" role="group" aria-label="Basemap">
        {BASEMAPS.map((option) => (
          <button
            key={option.id}
            type="button"
            className={basemap === option.id ? 'is-on' : ''}
            aria-pressed={basemap === option.id}
            onClick={() => selectBasemap(option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setIsDataLayersOpen(true)}
        className="sage-layers"
        aria-label="Open Data Layer"
      >
        <FiLayers className="w-4 h-4" />
        <span>Data Layer</span>
        {activeDataLayerCount > 0 ? (
          <span className="sage-layers-count">{activeDataLayerCount}</span>
        ) : null}
      </button>

      {hoverState ? (
        <div
          className="sage-hover pointer-events-none absolute z-30 -translate-x-1/2 -translate-y-full"
          style={{ left: hoverState.x, top: hoverState.y }}
        >
          <HoverCard props={hoverState.props} />
        </div>
      ) : null}

      <DataLayersDrawer
        isOpen={isDataLayersOpen}
        onClose={() => setIsDataLayersOpen(false)}
        groups={dataLayerGroups}
      />
    </div>
  );
}
